import { ArrowTopRightOnSquareIcon } from "@heroicons/react/16/solid";
import { useMediaQuery } from "@mantine/hooks";
import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { useState } from "react";
import { NavLink } from "react-router-dom";

import CompanyLogo from "~/components/CompanyLogo";
import Tooltip from "~/components/Tooltip";
import { useAuth } from "~/contexts/AuthContext";
import { LoadingValue } from "~/modules/dashboard/components/LoadingValue.tsx";
import { ProjectCardLock } from "~/modules/dashboard/components/ProjectCardLock.tsx";
import { useDashboardCalendar } from "~/modules/dashboard/hooks/useDashboardCalendar.ts";
import { useDashboardUser } from "~/modules/dashboard/hooks/useDashboardUser.tsx";
import type {
	UsersActiveStaffing,
	UsersActiveStaffingOrder,
	UsersActiveStaffingType,
} from "~/modules/project/api/staffing/staffingTypes.ts";
import { getPhaseOrderPath } from "~/modules/project/utils/projectDetailsViewUtils.ts";
import { monthlyClosingQueries } from "~/modules/timeTracking/api/monthlyClosing/monthlyClosingQueries.ts";
import type {
	MonthlyClosingCloseRequest,
	MonthlyClosingReopenRequest,
} from "~/modules/timeTracking/api/monthlyClosing/monthlyClosingTypes.ts";
import { CloseMonthlyClosingModal } from "~/modules/timeTracking/components/CloseMonthlyClosingModal.tsx";
import { getStaffingLabel } from "~/modules/timeTracking/components/closingLists/closingListTypes.ts";
import { ReopenMonthlyClosingModal } from "~/modules/timeTracking/components/ReopenMonthlyClosingModal.tsx";
import { formatTrackedDays } from "~/modules/timeTracking/utils/timeTrackingUtils.ts";
import { PermissionNames } from "~/types/entityNames.ts";
import { formatNumberWithComma } from "~/utils/numberUtils.ts";

// Matches the Tailwind breakpoint `lg`.
const LG_MEDIA_QUERY = "(min-width: 1024px)";

export type ProjectCardData = {
	project: UsersActiveStaffingType;
	phaseNumber: number;
	order: UsersActiveStaffingOrder;
	staffing: UsersActiveStaffing;
};

type Props = ProjectCardData & {
	trackedMinutes: number;
	trackedMinutesAreLoading: boolean;
};

const EMPTY_FIGURE = "–";

// The head line has the avatar, the title and a lock that closes or reopens the month of the staffing.
// The lock replaces a "…" menu: it has one action at a time, and a refused action shows the reason of
// the backend in a tooltip on the disabled lock.
// The title wraps to 2 lines, and its box always has the height of 2 lines, so all cards have the same
// height. A title of 1 line sits in the middle of the box, in line with the avatar. The tooltip shows the
// full title that the clamp cuts.
// "Verbleibende Tage" is `availableDays`, the budget of the whole staffing, not of the month.
// The card finds its part in the closing read of the month that the calendar shows, with the same key
// as the calendar, so no second request goes out. Without a part (no read yet, or a staffing that the
// read does not list) the card shows nothing about the closing: a guess from other fields would
// contradict the backend.
export function ProjectCard({ project, phaseNumber, order, staffing, trackedMinutes, trackedMinutesAreLoading }: Props) {
	const { hasAnyPermission } = useAuth();
	const isDesktop = useMediaQuery(LG_MEDIA_QUERY, undefined, { getInitialValueInEffect: false });
	// The project pages have no phone view, so a link below `lg` would lead to the blocker page. The
	// permission is the guard of the project details page.
	const canOpenOrder = isDesktop && hasAnyPermission(PermissionNames.CanManageProjects);
	const user = useDashboardUser();
	const { month } = useDashboardCalendar();
	const userId = user?.id ?? "";
	const { data: closing } = useQuery(monthlyClosingQueries.month(userId, month));
	const part = closing?.staffings.find((candidate) => candidate.staffingId === staffing.id);

	const [closeRequest, setCloseRequest] = useState<MonthlyClosingCloseRequest | null>(null);
	const [reopenRequest, setReopenRequest] = useState<MonthlyClosingReopenRequest | null>(null);

	const isOverBudget = staffing.availableDays !== null && staffing.availableDays < 0;
	const label = part ? getStaffingLabel(part, project.title) : "";

	return (
		<li className="flex min-w-0 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white">
			<div className="flex items-center gap-3 border-b border-gray-200 bg-gray-50 px-3 py-2.5">
				<CompanyLogo image={project.client.avatarImage}
							 displayName={project.client.displayName}
							 sizeClassName="size-9" />
				<div className="min-w-0 flex-1">
					<Tooltip text={project.title}>
						<h4 className="flex min-h-8 items-center text-xs font-semibold leading-4 text-gray-900">
							<span className="line-clamp-2">
								{canOpenOrder
									? (
										<NavLink className="inline hover:text-secondary-500"
												 to={getPhaseOrderPath(project.id, phaseNumber, order.id, "staffings")}>
											{project.title}
											<ArrowTopRightOnSquareIcon className="ml-1 inline size-3" />
										</NavLink>
									)
									: project.title}
							</span>
						</h4>
					</Tooltip>
				</div>
				<ProjectCardLock part={part}
								 onClose={() => part && setCloseRequest({
									 scope: { scope: "staffings", staffingIds: [part.staffingId] },
									 label,
								 })}
								 onReopen={() => part && setReopenRequest({
									 part: { part: "staffing", staffingId: part.staffingId },
									 label,
								 })} />
			</div>
			<dl className="flex flex-col gap-1 px-3 py-2.5 text-xs md:py-4">
				<div className="flex items-center justify-between gap-3">
					<dt className="text-gray-700">Gebuchte Tage in diesem Monat</dt>
					<dd className="font-semibold text-gray-900">
						{trackedMinutesAreLoading ? <LoadingValue size="sm" /> : formatTrackedDays(trackedMinutes)}
					</dd>
				</div>
				<div className="flex items-center justify-between gap-3">
					<dt className="text-gray-700">Verbleibende Tage</dt>
					<dd className={clsx("font-semibold", isOverBudget ? "text-red-600" : "text-gray-900")}>
						{staffing.availableDays === null ? EMPTY_FIGURE : formatNumberWithComma(staffing.availableDays, 1)}
					</dd>
				</div>
			</dl>
			<CloseMonthlyClosingModal month={month}
									  userId={userId}
									  request={closeRequest}
									  onClose={() => setCloseRequest(null)} />
			<ReopenMonthlyClosingModal month={month}
									   userId={userId}
									   request={reopenRequest}
									   onClose={() => setReopenRequest(null)} />
		</li>
	);
}
